<template>
  <div class="doc-check">
    <button
      type="button"
      class="check-btn"
      :disabled="scanning || !tags"
      @click="onCheck"
    >
      {{ scanning ? 'Scanning document…' : 'Review possible tag issues' }}
    </button>

    <template v-if="result">
      <div v-if="result.isClean" class="info-banner clean">
        No obvious tag issues found for <strong>{{ sourceTypeLabel }}</strong>.
      </div>

      <template v-else>
        <div class="warning-banner">
          Possibly unsupported tags — verify these before uploading. Some may
          be false positives (e.g. plain text that happens to look like a tag).
        </div>

        <div v-if="result.unknownScalars.length > 0" class="issue-group">
          <div class="issue-label">Scalar tags</div>
          <ul>
            <li v-for="tag in result.unknownScalars" :key="tag">
              <code>{{ formatScalar(tag) }}</code>
            </li>
          </ul>
        </div>

        <div v-if="result.unknownRegions.length > 0" class="issue-group">
          <div class="issue-label">Region names</div>
          <ul>
            <li v-for="name in result.unknownRegions" :key="name">
              <code>&#123;&#123;TableStart:{{ name }}&#125;&#125;</code> / <code>&#123;&#123;TableEnd:{{ name }}&#125;&#125;</code>
            </li>
          </ul>
        </div>

        <div v-if="result.unknownColumns.length > 0" class="issue-group">
          <div class="issue-label">Column tags</div>
          <ul>
            <li v-for="col in result.unknownColumns" :key="col">
              <code>&#123;&#123;{{ col }}&#125;&#125;</code>
            </li>
          </ul>
        </div>
      </template>
    </template>

    <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { scanDocumentTags } from '@/services/documentScanner';
import { validateTags, type ValidationResult } from '@/services/tagValidator';
import {
  sourceTypeLabels,
  type DocumentTemplateSourceType,
  type SupportedTags,
} from '@/types/SupportedTags';

const props = defineProps<{
  tags: SupportedTags | null;
}>();

const scanning = ref(false);
const result = ref<ValidationResult | null>(null);
const errorMessage = ref<string | null>(null);

// Same string-concat trick as elsewhere — Vue mustaches don't handle literal
// `{{` inside a `{{ ... }}` binding.
const OPEN = '{' + '{';
const CLOSE = '}' + '}';

function formatScalar(tag: string): string {
  return `${OPEN}${tag}${CLOSE}`;
}

const sourceTypeLabel = ref<string>('');
watch(
  () => props.tags?.sourceType,
  (v: DocumentTemplateSourceType | undefined) => {
    sourceTypeLabel.value = v !== undefined ? sourceTypeLabels[v] : '';
    // Also clear stale results — validating against a different inventory
    // would flag every previously-clean tag.
    result.value = null;
    errorMessage.value = null;
  },
  { immediate: true },
);

async function onCheck(): Promise<void> {
  if (!props.tags) return;
  scanning.value = true;
  errorMessage.value = null;
  try {
    const found = await scanDocumentTags();
    result.value = validateTags(found, props.tags);
  } catch (e) {
    errorMessage.value = e instanceof Error
      ? `Scan failed: ${e.message}`
      : 'Scan failed.';
    result.value = null;
  } finally {
    scanning.value = false;
  }
}
</script>

<style scoped>
.doc-check {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #edebe9;
}

.check-btn {
  width: 100%;
  padding: 6px 10px;
  font: inherit;
  color: #323130;
  background: #fff;
  border: 1px solid #8a8886;
  border-radius: 2px;
  cursor: pointer;
  transition: background 120ms ease;
}

.check-btn:hover:not(:disabled) {
  background: #f3f2f1;
}

.check-btn:disabled {
  color: #a19f9d;
  cursor: not-allowed;
}

.clean {
  padding: 8px 10px;
  background: #dff6dd;
  border-left: 3px solid #107c10;
  font-size: 12px;
  margin: 8px 0 4px;
}

.warning-banner {
  padding: 8px 10px;
  background: #fff4ce;
  border-left: 3px solid #dea100;
  font-size: 12px;
  margin: 8px 0;
}

.issue-group {
  margin: 8px 0;
}

.issue-label {
  font-size: 11px;
  color: #605e5c;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
}

.issue-group ul {
  padding-left: 18px;
  margin: 0;
  font-size: 12px;
}

.issue-group ul code {
  font-family: 'Consolas', 'Courier New', monospace;
  background: #f3f2f1;
  padding: 1px 4px;
  border-radius: 2px;
}
</style>
